import React from 'react';
import { useSearchParams } from 'react-router-dom';
import AppointmentsModal, {
    APPOINTMENT_PATIENT_QUERY_PARAM,
    APPOINTMENT_QUERY_PARAM
} from './AppointmentsModal';
import { PatientListProvider } from '../../providers/patient/PatientsListProvider';
import { APPOINTMENTS_QUERY_OPEN, APPOINTMENTS_QUERY_PARAM } from '../../utils/constants';

/**
 * Mounts AppointmentsModal at the app root (see PatientActionsContext.tsx)
 * so the `?appointments=open` deep link opens it from ANY page - not just
 * PatientsOverview, which used to be the only place it was mounted. Entry
 * points like PatientActionsContext's 'Schedule appointment' dispatch, or a
 * pasted deep link while on a patient action page, now reach the same
 * modal instance without a navigation.
 *
 * Owns the same push-on-open / replace-on-close cleanup for
 * `?appointments=` that PatientsOverview used to own directly;
 * AppointmentsModal itself still owns `?appointment=`.
 *
 * Wrapped in its own PatientListProvider (rather than requiring one further
 * up the tree, which would mean fetching the patients list on every page)
 * so the modal's patient picker works regardless of which page is mounted
 * underneath - it shares the same `['patients','dashboard',...]` query
 * cache as PatientsOverview's own provider instance, so opening it from
 * Home doesn't refetch.
 */
const GlobalAppointmentsModal: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isOpen = searchParams.get(APPOINTMENTS_QUERY_PARAM) === APPOINTMENTS_QUERY_OPEN;

    if (!isOpen) return null;

    const handleClose = () => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete(APPOINTMENTS_QUERY_PARAM);
                next.delete(APPOINTMENT_QUERY_PARAM);
                next.delete(APPOINTMENT_PATIENT_QUERY_PARAM);
                return next;
            },
            { replace: true }
        );
    };

    return (
        // refetchOnMount={false}: this provider mounts every time the modal
        // opens; the default 'always' would force a refetch of the shared
        // ['patients','dashboard',...] query and visibly refresh the patients
        // table underneath. The picker is fine with cached data (still fetches
        // when the cache is empty, e.g. a deep link on a non-patients page).
        <PatientListProvider refetchOnMount={false}>
            <AppointmentsModal onClose={handleClose} />
        </PatientListProvider>
    );
};

export default GlobalAppointmentsModal;
